// The money metrics, read from the ledger (spec/17 §3 M-01, M-02, M-03, M-13, M-19; F-ANL-01).
//
// **One definition per metric** (spec/17 §1.1). Every surface that shows revenue — the venue's Reports overview,
// the organisation's venue comparison, the floor's "takings today", the day close's review, HQ Live — hands the
// ledger's activity for its window to these functions and shows what they return. None of them adds tenders up on
// its own, which is how the old system ended up with a different revenue on every screen (spec/17 §8).
//
// **The input is the ledger, not a flag** (spec/17 §1.4). A `LedgerActivityCell` is one (entry kind, account kind,
// owner type) cell of the journal lines in a window, debits and credits apart — exactly what
// `LedgerService.venueActivityBetween` returns. Nothing here reads a sale's `status` or an `isPaid` boolean, so
// flipping one changes nothing.
//
// **M-01 Revenue** is the settled money that reached the business, net of what went back:
//
//   payment accounts   tender_cash, tender_counter_card, tender_counter_upi (the venue's own till, card machine and
//                      QR), provider_clearing (online payments the provider captured) and wallet_cash (a player's
//                      real wallet money, **counted when it is spent**, never when it is topped up).
//   payments           the debits to those accounts;   refunds   the credits to them;   revenue = payments − refunds.
//
// Why that is the whole rule, entry by entry (spec/06 §2.3 postings):
//
//   sale_settlement      tender +Δ …                                 a payment. Split tenders are one debit each.
//   wallet_spend         wallet_cash +Δ · tender_wallet −Δ           the wallet share of a sale: a payment when spent.
//   segment_charge       wallet_cash +Δ · sales_play −Δ              play paid from the wallet: a payment.
//                        receivable +Δ · sales_play −Δ               postpaid play: **not** a payment until settled.
//   receivable_settlement tender +Δ · receivable −Δ                  on-account settled: a payment now.
//   refund / pass_refund tender −Δ …                                 a refund.
//   wallet_sale_refund   tender_wallet +Δ · wallet_cash −Δ           money back into the wallet: a refund.
//   segment_charge_reversal  sales_play +Δ · wallet_cash −Δ          play money back into the wallet: a refund.
//   wallet_topup         tender +Δ · wallet_cash −Δ                  **nets to zero** — a top-up is not revenue.
//   wallet_refund        wallet_cash +Δ · tender −Δ                  nets to zero — the top-up handed back.
//   topup_bonus          sales_other +B · wallet_bonus −B            no payment account: bonus money is not money.
//
// A top-up and its hand-back already net to zero across the payment accounts, but they are also named in
// `REVENUE_EXCLUDED_ENTRY_KINDS`, so their gross debits and credits do not inflate "payments" and "refunds". A
// wallet adjustment (a manual correction) and a provider's payout to the bank move money between the business's own
// accounts and are excluded for the same reason. Failed and retried payments never post (spec/06 §1.8), so they
// cannot be counted.
//
// **Empty is not zero** (spec/17 §1.5). A window with no line on any payment account returns `null`, and a surface
// shows "no data" — never a `0` it did not measure.
import { roundHalfUpBigInt } from "../money/round.js";
/**
 * The entry that posts an online payment's commission and provider fee when it is captured (ADR-0061; spec/06 §2.3):
 * `platform_fee +commission · provider_fee +fee · provider_clearing −(commission + fee)`.
 */
export const ONLINE_PAYMENT_FEES_ENTRY_KIND = 'online_payment_fees';
/** The entry that gives a refunded online payment's commission back, pro rata: `provider_clearing +c · platform_fee −c`. */
export const ONLINE_REFUND_FEES_ENTRY_KIND = 'online_refund_fees';
/** How money reached the business (spec/17 R-01 "tenders split"). */
export const REVENUE_TENDERS = ['cash', 'counter_card', 'counter_upi', 'online', 'wallet'];
/** The ledger account each tender's money lands in (spec/06 §2.1). */
export const PAYMENT_ACCOUNT_KINDS = {
    cash: 'tender_cash',
    counter_card: 'tender_counter_card',
    counter_upi: 'tender_counter_upi',
    online: 'provider_clearing',
    wallet: 'wallet_cash',
};
/**
 * Wallet cash lives in two accounts (ADR-0048): ordinary cash, and tax-paid cash carried over from a café's previous
 * system with GST already paid on it at top-up. Both are the player's real money: spending either is wallet revenue.
 */
export const WALLET_CASH_ACCOUNT_KINDS = new Set(['wallet_cash', 'wallet_cash_tax_paid']);
/**
 * Cash carried over from a café's previous system and held for a record nobody has claimed yet (ADR-0048). It is owed
 * to players as surely as a wallet's cash — the café took that money — so M-04 and the M-19 balance count it with the
 * wallets'. (Carried-over *time* is seconds, held in `carried_over_time`, and is not valued as money here.)
 */
export const CARRIED_OVER_CASH_ACCOUNT_KINDS = new Set(['carried_over_cash', 'carried_over_cash_tax_paid']);
/** Every account kind the wallets' outstanding balance (`walletBalanceOf`) reads: what the server asks the ledger for. */
export const WALLET_BALANCE_ACCOUNT_KINDS = [...WALLET_CASH_ACCOUNT_KINDS, 'wallet_bonus', ...CARRIED_OVER_CASH_ACCOUNT_KINDS];
const TENDER_BY_ACCOUNT = new Map([
    ...Object.entries(PAYMENT_ACCOUNT_KINDS).map(([tender, kind]) => [kind, tender]),
    ['wallet_cash_tax_paid', 'wallet'],
]);
/** Every account a payment lands in, wallet cash in both its accounts (M-01). */
export const PAYMENT_ACCOUNT_KIND_SET = new Set(TENDER_BY_ACCOUNT.keys());
/**
 * Entries that move money between the business's own accounts, or in and out of a player's wallet, and are
 * therefore never revenue: a top-up (counted when the wallet is spent), its hand-back, a manual wallet correction,
 * and a provider's payout of captured money to the bank.
 */
export const REVENUE_EXCLUDED_ENTRY_KINDS = new Set([
    'wallet_topup',
    'wallet_refund',
    'wallet_adjustment',
    'provider_settlement',
    // The Operator's commission and the provider's fee on an online payment, and the commission a refund gives back
    // (ADR-0061): the organisation's costs of being paid online, taken from what the provider holds for it
    // (`provider_clearing`), never a reduction of revenue or of money taken.
    ONLINE_PAYMENT_FEES_ENTRY_KIND,
    ONLINE_REFUND_FEES_ENTRY_KIND,
]);
/** A cell of the window's activity that M-01 reads: a payment account, on an entry that can be revenue. */
function isRevenueCell(cell) {
    return TENDER_BY_ACCOUNT.has(cell.accountKind) && !REVENUE_EXCLUDED_ENTRY_KINDS.has(cell.entryKind);
}
/**
 * **M-01 Revenue** (spec/17 §3): settled payments minus settled refunds, excluding on-account until settled and
 * wallet top-ups until spent. `null` when the window has no line on any payment account.
 *
 * ```ts
 * revenueOf([{ entryKind: 'sale_settlement', accountKind: 'tender_cash', ownerType: 'venue', debit: 25000n, credit: 0n },
 *            { entryKind: 'sale_settlement', accountKind: 'tender_counter_upi', ownerType: 'venue', debit: 25000n, credit: 0n },
 *            { entryKind: 'refund', accountKind: 'tender_cash', ownerType: 'venue', debit: 0n, credit: 10000n }])
 * // { total: 40000n, payments: 50000n, refunds: 10000n, byTender: { cash: 15000n, counter_upi: 25000n, … } }
 * ```
 */
export function revenueOf(cells) {
    const byTender = { cash: 0n, counter_card: 0n, counter_upi: 0n, online: 0n, wallet: 0n };
    let payments = 0n;
    let refunds = 0n;
    let seen = false;
    for (const cell of cells) {
        if (!isRevenueCell(cell))
            continue;
        const tender = TENDER_BY_ACCOUNT.get(cell.accountKind);
        if (tender === undefined)
            continue;
        seen = true;
        payments += cell.debit;
        refunds += cell.credit;
        byTender[tender] += cell.debit - cell.credit;
    }
    if (!seen)
        return null;
    return { total: payments - refunds, payments, refunds, byTender };
}
// ---------------------------------------------------------------------------------------------
// M-02 Net sales by category
// ---------------------------------------------------------------------------------------------
/** The sales categories the ledger credits (spec/06 §2.1 `sales_*`). */
export const SALES_CATEGORIES = ['play', 'food', 'retail', 'membership', 'other'];
/** The `sales_*` account each category credits (spec/06 §2.1). */
export const SALES_ACCOUNT_KINDS = {
    play: 'sales_play',
    food: 'sales_fnb',
    retail: 'sales_retail',
    membership: 'sales_membership',
    other: 'sales_other',
};
const CATEGORY_BY_ACCOUNT = new Map(Object.entries(SALES_ACCOUNT_KINDS).map(([category, kind]) => [kind, category]));
/** The contra-revenue account a discount debits (spec/06 §2.1, §8.4). */
export const DISCOUNTS_ACCOUNT_KIND = 'discounts';
/**
 * Entries that put credit the venue gives away into a wallet (spec/06 §2.3): a top-up bonus, a coupon's wallet grant,
 * and a manual wallet adjustment. Each posts `sales_other` (promo expense, contra) against the wallet, so it is not a
 * sale of anything: M-02 and the day close report it on its own line (wallet credit given) rather than as a negative
 * "Other" sale (2026-10-01 day close; 2026-10-02 Reports).
 */
export const WALLET_CREDIT_ENTRY_KINDS = new Set(['topup_bonus', 'coupon_grant', 'wallet_adjustment']);
/**
 * **M-02 Net sales by category** (spec/17 §3): the ledger's `sales_*` credits net of what refunds and reversals debit
 * back, ex-tax (tax sits in `tax_payable`, never in a sales account), with the discounts contra account and the wallet
 * credit given taken off the total (the total, and so M-22, is unchanged by keeping the credit out of the categories).
 * `null` when the window touched no sales or discounts account.
 */
export function netSalesOf(cells) {
    const byCategory = { play: 0n, food: 0n, retail: 0n, membership: 0n, other: 0n };
    let discounts = 0n;
    let walletCredit = 0n;
    let seen = false;
    for (const cell of cells) {
        const category = CATEGORY_BY_ACCOUNT.get(cell.accountKind);
        if (category !== undefined) {
            seen = true;
            if (WALLET_CREDIT_ENTRY_KINDS.has(cell.entryKind))
                walletCredit += cell.debit - cell.credit;
            else
                byCategory[category] += cell.credit - cell.debit;
        }
        else if (cell.accountKind === DISCOUNTS_ACCOUNT_KIND) {
            seen = true;
            discounts += cell.debit - cell.credit;
        }
    }
    if (!seen)
        return null;
    const gross = SALES_CATEGORIES.reduce((sum, category) => sum + byCategory[category], 0n);
    return { byCategory, discounts, walletCredit, total: gross - discounts - walletCredit };
}
// ---------------------------------------------------------------------------------------------
// M-03 Tax collected
// ---------------------------------------------------------------------------------------------
/** The prefix of the tax accounts: `tax_payable:CGST`, `tax_payable:SGST`, `tax_payable:IGST` (spec/06 §2.1). */
export const TAX_PAYABLE_PREFIX = 'tax_payable:';
/**
 * **M-03 Tax collected** (spec/17 §3): the net credit to `tax_payable:*` by component — what `regular` orders posted,
 * less what their refunds released. `composition` and `unregistered` orders post no tax line, so they add nothing.
 * `null` when no tax account moved.
 */
export function taxCollectedOf(cells) {
    const byComponent = {};
    let seen = false;
    for (const cell of cells) {
        if (!cell.accountKind.startsWith(TAX_PAYABLE_PREFIX))
            continue;
        seen = true;
        const component = cell.accountKind.slice(TAX_PAYABLE_PREFIX.length);
        byComponent[component] = (byComponent[component] ?? 0n) + cell.credit - cell.debit;
    }
    if (!seen)
        return null;
    const sorted = Object.fromEntries(Object.entries(byComponent).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
    return { byComponent: sorted, total: Object.values(byComponent).reduce((sum, value) => sum + value, 0n) };
}
/** The prefix of the accounts holding GST already paid at top-up in a café's previous system (ADR-0048). */
export const TAX_PAID_AT_TOPUP_PREFIX = 'tax_paid_at_topup:';
/**
 * Beside M-03: the GST on sales and play paid for with tax-paid wallet money — carried over from a café's previous
 * system, GST paid at top-up there (ADR-0048) — by component: the net credit to `tax_paid_at_topup:*`. It is not tax
 * collected (M-03 counts `tax_payable` only), so it is never collected or filed twice; the report shows it apart.
 * `null` when no such account moved.
 */
export function taxPaidAtTopUpOf(cells) {
    const byComponent = {};
    let seen = false;
    for (const cell of cells) {
        if (!cell.accountKind.startsWith(TAX_PAID_AT_TOPUP_PREFIX))
            continue;
        seen = true;
        const component = cell.accountKind.slice(TAX_PAID_AT_TOPUP_PREFIX.length);
        byComponent[component] = (byComponent[component] ?? 0n) + cell.credit - cell.debit;
    }
    if (!seen)
        return null;
    const sorted = Object.fromEntries(Object.entries(byComponent).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
    return { byComponent: sorted, total: Object.values(byComponent).reduce((sum, value) => sum + value, 0n) };
}
/** `numerator ÷ denominator` in basis points, half up; `null` when the denominator is not positive. */
export function ratioOf(numerator, denominator) {
    if (denominator <= 0n)
        return null;
    return { numerator, denominator, basisPoints: Number(roundHalfUpBigInt(numerator * 10000n, denominator)) };
}
/**
 * **M-13 Refund rate** (spec/17 §3): refunded amount ÷ revenue, both from M-01 (refunds over the payments they are
 * netted from, so a day that refunded everything reads 100 %, not a division by zero). `null` without revenue.
 */
export function refundRateOf(revenue) {
    if (revenue === null)
        return null;
    return ratioOf(revenue.refunds, revenue.payments);
}
const WALLET_ACCOUNTS = new Set(['wallet_cash', 'wallet_cash_tax_paid', 'wallet_bonus']);
const WALLET_SPEND_ENTRIES = new Set(['wallet_spend', 'segment_charge']);
/**
 * **M-19 Wallet** flows (spec/17 §3): top-ups, bonus granted, spent and refunded in the window. `null` when no wallet
 * account moved.
 */
export function walletFlowsOf(cells) {
    let topUps = 0n;
    let bonusGranted = 0n;
    let spent = 0n;
    let spentCash = 0n;
    let refunded = 0n;
    let seen = false;
    for (const cell of cells) {
        if (cell.entryKind === 'wallet_topup' && !WALLET_ACCOUNTS.has(cell.accountKind)) {
            seen = true;
            topUps += cell.debit;
        }
        if (!WALLET_ACCOUNTS.has(cell.accountKind))
            continue;
        seen = true;
        if (cell.entryKind === 'topup_bonus' && cell.accountKind === 'wallet_bonus')
            bonusGranted += cell.credit;
        if (WALLET_SPEND_ENTRIES.has(cell.entryKind)) {
            spent += cell.debit;
            if (WALLET_CASH_ACCOUNT_KINDS.has(cell.accountKind))
                spentCash += cell.debit;
        }
        if (cell.entryKind === 'wallet_refund' && WALLET_CASH_ACCOUNT_KINDS.has(cell.accountKind))
            refunded += cell.debit;
    }
    if (!seen)
        return null;
    return { topUps, bonusGranted, spent, spentCash, refunded };
}
/**
 * The wallets' outstanding balance (M-19 "balance outstanding", and M-04's wallet part) from the raw ledger balances
 * of the `WALLET_BALANCE_ACCOUNT_KINDS`: the wallets' cash (ordinary and tax-paid) and bonus, and the cash carried over
 * for unclaimed records, which counts as cash — the business owes it all the same. All are credit-normal (a raw balance
 * ≤ 0 is money the business owes), so the natural balance is the negation. `null` when there are no such accounts.
 */
export function walletBalanceOf(rawBalances) {
    let cash = 0n;
    let bonus = 0n;
    let seen = false;
    for (const row of rawBalances) {
        if (WALLET_CASH_ACCOUNT_KINDS.has(row.accountKind) || CARRIED_OVER_CASH_ACCOUNT_KINDS.has(row.accountKind)) {
            seen = true;
            cash -= row.amount;
        }
        else if (row.accountKind === 'wallet_bonus') {
            seen = true;
            bonus -= row.amount;
        }
    }
    return seen ? { cash, bonus } : null;
}
// ---------------------------------------------------------------------------------------------
// Combining windows
// ---------------------------------------------------------------------------------------------
/**
 * The cells of several windows as one: cells with the same entry kind, account kind and owner type summed. A range
 * of days is its days' cells merged — the ledger's activity is additive — so a report that reads some days from the
 * rollup and the rest live computes exactly what one live read of the whole range would.
 */
export function mergeActivityCells(...windows) {
    const merged = new Map();
    for (const cells of windows) {
        for (const cell of cells) {
            const key = `${cell.entryKind}\u0000${cell.accountKind}\u0000${cell.ownerType}`;
            const current = merged.get(key);
            if (current === undefined)
                merged.set(key, { entryKind: cell.entryKind, accountKind: cell.accountKind, ownerType: cell.ownerType, debit: cell.debit, credit: cell.credit });
            else {
                current.debit += cell.debit;
                current.credit += cell.credit;
            }
        }
    }
    return [...merged.values()];
}
//# sourceMappingURL=ledger-metrics.js.map